import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  MapPin, Briefcase, DollarSign, Clock, ExternalLink, Mail,
  ArrowLeft, Building2, Share2, CheckCircle
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { authHeaders } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TYPE_COLORS = {
  "full-time": { color: "#2ECC71", bg: "rgba(46,204,113,0.1)" },
  "part-time": { color: "#38bdf8", bg: "rgba(56,189,248,0.1)" },
  "contract":  { color: "#facc15", bg: "rgba(250,204,21,0.1)" },
  "remote":    { color: "#a78bfa", bg: "rgba(167,139,250,0.1)" },
  "hybrid":    { color: "#fb923c", bg: "rgba(251,146,60,0.1)" },
};

function salaryDisplay(job) {
  if (!job.salary_min && !job.salary_max) return null;
  const fmt = (n) => `$${Number(n).toLocaleString()}`;
  const period = job.salary_period === "hour" ? " / hour" : " / year";
  if (job.salary_min && job.salary_max) return `${fmt(job.salary_min)} – ${fmt(job.salary_max)}${period}`;
  if (job.salary_min) return `${fmt(job.salary_min)}+${period}`;
  return `Up to ${fmt(job.salary_max)}${period}`;
}

export default function JobDetail() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/jobs/${jobId}`, { headers: authHeaders() })
      .then(r => setJob(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [jobId]);

  const share = () => {
    navigator.clipboard?.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const daysLeft = job?.expires_at
    ? Math.max(0, Math.ceil((new Date(job.expires_at) - new Date()) / 86400000))
    : null;

  if (loading) return (
    <div className="min-h-screen" style={{ background: "#f8fafc" }}>
      <Navbar />
      <div className="flex items-center justify-center py-32">
        <div className="w-8 h-8 border-2 border-[#003366] border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );

  if (!job) return (
    <div className="min-h-screen" style={{ background: "#f8fafc" }}>
      <Navbar />
      <div className="section-container py-20 text-center">
        <Briefcase size={48} className="text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-600">Job not found</h2>
        <p className="text-slate-400 mt-1 mb-5">This listing may have expired or been removed.</p>
        <Link to="/jobs" className="inline-flex items-center gap-2 font-bold px-5 py-3 rounded-xl text-white text-sm"
          style={{ background: "#003366" }}>
          <ArrowLeft size={14} /> Browse All Jobs
        </Link>
      </div>
      <Footer />
    </div>
  );

  const typeStyle = TYPE_COLORS[job.job_type] || { color: "#64748b", bg: "rgba(100,116,139,0.1)" };
  const salary = salaryDisplay(job);

  return (
    <div className="min-h-screen" style={{ background: "#f8fafc" }}>
      <SEOHead
        title={`${job.title} at ${job.company_name}`}
        description={`${job.company_name} is hiring a ${job.title}. ${job.description?.slice(0, 140)}...`}
        canonical={`/jobs/${job.id}`}
        jsonLd={{
          "@type": "JobPosting",
          "title": job.title,
          "description": job.description,
          "hiringOrganization": { "@type": "Organization", "name": job.company_name },
          "employmentType": job.job_type?.toUpperCase().replace("-", "_"),
          "datePosted": job.posted_at,
          "validThrough": job.expires_at,
          "jobLocation": job.locations?.map(l => ({
            "@type": "Place",
            "address": { "@type": "PostalAddress", "postalCode": l.zip_code, "addressRegion": l.state }
          })),
          ...(job.salary_min || job.salary_max ? {
            "baseSalary": {
              "@type": "MonetaryAmount",
              "currency": "USD",
              "value": {
                "@type": "QuantitativeValue",
                "minValue": job.salary_min,
                "maxValue": job.salary_max,
                "unitText": job.salary_period === "hour" ? "HOUR" : "YEAR"
              }
            }
          } : {})
        }}
      />
      <Navbar />

      {/* Header bar */}
      <div style={{ background: "white", borderBottom: "1px solid #e2e8f0" }}>
        <div className="section-container py-3 flex items-center gap-2">
          <Link to="/jobs" className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 text-sm font-medium transition-colors">
            <ArrowLeft size={14} /> Jobs
          </Link>
          <span className="text-slate-300">/</span>
          <span className="text-slate-600 text-sm truncate">{job.title}</span>
        </div>
      </div>

      <div className="section-container py-8">
        <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Main content */}
          <div className="lg:col-span-2 space-y-5">
            {/* Job header */}
            <div className="rounded-2xl p-6" style={{ background: "white", border: "1px solid #e2e8f0" }}>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                  style={{ background: "#f1f5f9", border: "1px solid #e2e8f0" }}>
                  {job.company_logo_url
                    ? <img src={job.company_logo_url} alt={job.company_name} className="w-full h-full object-cover" />
                    : <Building2 size={28} className="text-slate-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl font-black text-[#0F172A] leading-tight">{job.title}</h1>
                  <p className="text-slate-500 font-semibold mt-0.5">{job.company_name}</p>
                </div>
                <span className="text-sm font-bold px-3 py-1.5 rounded-full capitalize flex-shrink-0"
                  style={{ color: typeStyle.color, background: typeStyle.bg }}>
                  {job.job_type}
                </span>
              </div>

              <div className="flex flex-wrap gap-4">
                {job.locations?.map((l, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-sm text-slate-500">
                    <MapPin size={13} /> {l.city ? `${l.city}, ${l.state} ${l.zip_code}` : l.zip_code}
                  </span>
                ))}
                {salary && (
                  <span className="flex items-center gap-1.5 text-sm text-slate-500">
                    <DollarSign size={13} /> {salary}
                  </span>
                )}
                {daysLeft !== null && (
                  <span className={`flex items-center gap-1.5 text-sm font-semibold ${daysLeft <= 7 ? "text-red-400" : daysLeft <= 14 ? "text-amber-500" : "text-slate-400"}`}>
                    <Clock size={13} /> {daysLeft} days left
                  </span>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="rounded-2xl p-6" style={{ background: "white", border: "1px solid #e2e8f0" }}>
              <h2 className="text-lg font-black text-[#0F172A] mb-4">About This Role</h2>
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">{job.description}</p>
              </div>
            </div>
          </div>

          {/* Apply sidebar */}
          <div className="space-y-4">
            <div className="rounded-2xl p-5 sticky top-24"
              style={{ background: "white", border: "1px solid #e2e8f0" }}>
              <h3 className="font-black text-[#0F172A] text-base mb-4">Apply for this Role</h3>

              {job.apply_url && (
                <a href={job.apply_url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full font-bold py-3.5 rounded-xl text-white text-sm transition-all hover:scale-105 mb-3"
                  style={{ background: "linear-gradient(135deg, #003366, #00509e)", boxShadow: "0 4px 16px rgba(0,51,102,0.3)" }}
                  data-testid="apply-url-btn">
                  <ExternalLink size={15} /> Apply Now
                </a>
              )}

              {job.apply_email && (
                <a href={`mailto:${job.apply_email}?subject=Application: ${encodeURIComponent(job.title)}`}
                  className="flex items-center justify-center gap-2 w-full font-bold py-3 rounded-xl text-sm transition-all hover:brightness-105 mb-3"
                  style={{
                    background: "rgba(0,51,102,0.06)",
                    color: "#003366",
                    border: "1.5px solid rgba(0,51,102,0.2)"
                  }}
                  data-testid="apply-email-btn">
                  <Mail size={14} /> Email Application
                </a>
              )}

              <button onClick={share}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-semibold transition-all"
                style={{ border: "1px solid #e2e8f0", color: copied ? "#2ECC71" : "#64748b" }}
                data-testid="share-job-btn">
                {copied ? <><CheckCircle size={14} /> Copied!</> : <><Share2 size={14} /> Share Job</>}
              </button>

              <div className="mt-4 pt-4" style={{ borderTop: "1px solid #f1f5f9" }}>
                <p className="text-xs text-slate-400 text-center">
                  Posted {job.posted_at ? new Date(job.posted_at).toLocaleDateString() : "recently"}<br />
                  Expires {daysLeft !== null ? `in ${daysLeft} days` : "soon"}
                </p>
              </div>
            </div>

            {/* Post your own job CTA */}
            <div className="rounded-2xl p-4 text-center"
              style={{ background: "rgba(0,51,102,0.04)", border: "1px dashed rgba(0,51,102,0.15)" }}>
              <Briefcase size={20} className="text-[#003366] mx-auto mb-2" />
              <p className="text-sm font-bold text-[#003366]">Hiring too?</p>
              <p className="text-xs text-slate-400 mt-0.5 mb-3">Post a job for $50 — 90-day listing</p>
              <Link to="/post-a-job"
                className="inline-flex items-center gap-1.5 font-bold px-4 py-2 rounded-lg text-white text-xs transition-all hover:brightness-110"
                style={{ background: "linear-gradient(135deg, #003366, #00509e)" }}>
                Post a Job
              </Link>
            </div>
          </div>

        </div>
      </div>
      <Footer />
    </div>
  );
}
